import { create } from 'zustand'

import type { ComparisonRow } from '@/engines/comparison'
import { quoteManager } from '@/engines/quote'
import type {
  QuoteDocumentPro,
  QuoteSidebarCategory,
  QuoteTemplateDefinition,
} from '@/engines/quote'

interface QuoteBuilderProState {
  ready: boolean
  document: QuoteDocumentPro
  category: QuoteSidebarCategory
  query: string
  results: ComparisonRow[]
  templates: QuoteTemplateDefinition[]
  history: QuoteDocumentPro[]

  hydrate: () => void
  newDocument: () => void
  setTitle: (title: string) => void
  setCategory: (category: QuoteSidebarCategory) => void
  setQuery: (query: string) => void
  search: () => void
  addComparisonRow: (row: ComparisonRow) => void
  addFromComparisonSelection: () => void
  addService: (serviceId: string) => void
  applyTemplate: (templateId: string) => void
  updateBlock: (blockId: string, patch: Parameters<typeof quoteManager.updateBlock>[1]) => void
  removeBlock: (blockId: string) => void
  duplicateBlock: (blockId: string) => void
  moveBlock: (from: number, to: number) => void
  setGlobalMargin: (value: number | null) => void
  setGlobalDiscount: (percent: number, value: number) => void
  save: () => void
  load: (id: string) => void
  exportPdf: () => ReturnType<typeof quoteManager.exportPdf>
}

function sync(set: (partial: Partial<QuoteBuilderProState>) => void) {
  set({
    ready: true,
    document: quoteManager.getDocument(),
    templates: quoteManager.listTemplates(),
    history: quoteManager.listHistory(),
  })
}

export const useQuoteBuilderProStore = create<QuoteBuilderProState>((set, get) => ({
  ready: false,
  document: quoteManager.getDocument(),
  category: 'Desktop',
  query: '',
  results: [],
  templates: [],
  history: [],

  hydrate: () => {
    quoteManager.restoreAutosave()
    sync(set)
    get().search()
  },

  newDocument: () => {
    quoteManager.newDocument()
    sync(set)
  },

  setTitle: (title) => {
    quoteManager.setDocument({ ...quoteManager.getDocument(), title })
    sync(set)
  },

  setCategory: (category) => {
    set({ category })
    get().search()
    set({ templates: quoteManager.listTemplates(category) })
  },

  setQuery: (query) => set({ query }),

  search: () => {
    const { query, category } = get()
    const results = quoteManager.searchComparison(query || category, category)
    set({ results, templates: quoteManager.listTemplates(category) })
  },

  addComparisonRow: (row) => {
    const best = row.offers.find((o) => o.isBestPrice) ?? row.offers[0]
    if (!best) return
    quoteManager.addComparisonOffer(best)
    sync(set)
  },

  addFromComparisonSelection: () => {
    quoteManager.addSelectedFromComparison()
    sync(set)
  },

  addService: (serviceId) => {
    quoteManager.addService(serviceId)
    sync(set)
  },

  applyTemplate: (templateId) => {
    quoteManager.applyTemplate(templateId)
    sync(set)
  },

  updateBlock: (blockId, patch) => {
    quoteManager.updateBlock(blockId, patch)
    sync(set)
  },

  removeBlock: (blockId) => {
    quoteManager.removeBlock(blockId)
    sync(set)
  },

  duplicateBlock: (blockId) => {
    quoteManager.duplicateBlock(blockId)
    sync(set)
  },

  moveBlock: (from, to) => {
    quoteManager.moveBlock(from, to)
    sync(set)
  },

  setGlobalMargin: (value) => {
    quoteManager.setGlobalMargin(value)
    sync(set)
  },

  setGlobalDiscount: (percent, value) => {
    quoteManager.setGlobalDiscount(percent, value)
    sync(set)
  },

  save: () => {
    quoteManager.save()
    sync(set)
  },

  load: (id) => {
    quoteManager.load(id)
    sync(set)
  },

  exportPdf: () => quoteManager.exportPdf(),
}))
